import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { apiGet, apiSend, fmtDate } from '../api';
import { alertFailure } from '../utils/sessionEnd';
import { theme, card, btnPrimary, btnSecondary, input } from '../theme';
// v0.46.7 — Shared page title (standardised heading size).
import PageHeading from '../components/PageHeading';
import ShareReportModal from '../components/ShareReportModal';
// v0.17.1 — Lucide migration.
import { Link2, Check, Copy } from 'lucide-react';

// SharesScreen - lists every share link the operator has minted for
// this project, with revoke + new-share controls.
//
// Route: /projects/:projectId/shares
//
// v0.8.0 - initial. Companion to ShareReportModal + /api/report-shares.
// v0.265.0 - each link says what its holder can do, and a live one can be
// switched (Brett: "let me switch the link" — a View link sent by mistake
// becomes Reply without sending a new one). The holder sees it next time
// the page loads. A system link (another system's channel) only says so.
const ACCESS = [
  ['view', 'View only'],
  ['reply', 'Reply'],
  ['rectify', 'Rectify'],
  ['owner', 'Owner sign-off'],
];
const accessLabel = (p) => (ACCESS.find(([k]) => k === p) || ACCESS[0])[1];

export default function SharesScreen() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeAll, setIncludeAll] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [switchingId, setSwitchingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const qs = includeAll ? '&includeAll=1' : '';
      const [p, sh] = await Promise.all([
        apiGet(`/api/projects/${projectId}`),
        apiGet(`/api/report-shares?projectId=${projectId}${qs}`),
      ]);
      setProject(p);
      setShares(Array.isArray(sh) ? sh : []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, includeAll]);

  async function revoke(id) {
    if (!confirm('Revoke this share link? The recipient will see a "link expired" page immediately.')) return;
    setRevokingId(id);
    try {
      await apiSend('PATCH', `/api/report-shares/${id}`, { revokedAt: 'now' });
      await load();
    } catch (e) {
      alertFailure('Revoke failed: ' + (e.message || String(e)), e);
    } finally {
      setRevokingId(null);
    }
  }

  async function switchAccess(id, permission) {
    setSwitchingId(id);
    try {
      await apiSend('PATCH', `/api/report-shares/${id}`, { permission });
      await load();
    } catch (e) {
      alertFailure('Could not change the link: ' + (e.message || String(e)), e);
    } finally {
      setSwitchingId(null);
    }
  }

  function copyUrl(share) {
    try {
      navigator.clipboard.writeText(share.url);
      setCopiedId(share.id);
      setTimeout(() => setCopiedId((cur) => (cur === share.id ? null : cur)), 1600);
    } catch { /* clipboard unavailable - user can copy manually */ }
  }

  function shareStatus(s) {
    if (s.revokedAt) return { label: 'revoked', color: theme.danger };
    if (s.expiresAt && new Date(s.expiresAt) < new Date()) return { label: 'expired', color: theme.textLight };
    // v0.271.0 - the job this report is for was closed in the sending system:
    // the link has stopped, and starts again if the job is reopened.
    if (s.linksClosedAt) return { label: 'job closed', color: theme.warn };
    return { label: 'active', color: theme.success };
  }

  if (loading) {
    return (
      <div>
        <button onClick={() => navigate(`/projects/${projectId}`)} style={{ ...btnSecondary, marginBottom: 12 }}>← Back</button>
        <div style={{ ...card, padding: 40, textAlign: 'center', color: theme.textLight }}>Loading shares…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <button onClick={() => navigate(`/projects/${projectId}`)} style={{ ...btnSecondary, marginBottom: 12 }}>← Back</button>
        <div style={{ ...card, color: theme.danger }}>{error}</div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate(`/projects/${projectId}`)} style={{ ...btnSecondary, marginBottom: 12 }}>← Back to project</button>

      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <PageHeading style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Link2 size={20} /> Shared reports</PageHeading>
            <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4 }}>
              {project?.Name || project?.name || 'Location'} · {shares.length} share{shares.length === 1 ? '' : 's'}
              {!includeAll && <span> · active only</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: theme.textLight, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeAll}
                onChange={(e) => setIncludeAll(e.target.checked)}
              />
              Show revoked + expired
            </label>
            <button onClick={() => setShowModal(true)} style={btnPrimary}>+ New share</button>
          </div>
        </div>
      </div>

      {/* v0.265.0 - scrolls sideways on a phone; 'hidden' cut off Status and Revoke. */}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {shares.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center', color: theme.textLight }}>
            <Link2 size={36} color={theme.textLight} strokeWidth={1.5} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, marginBottom: 4 }}>No share links yet</div>
            <div style={{ fontSize: 12 }}>Click "New share" to mint a read-only URL for a builder, supervisor or client.</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: theme.bg, borderBottom: `1px solid ${theme.border}` }}>
                <Th>Created</Th>
                <Th>Recipient</Th>
                <Th>Access</Th>
                <Th>Status</Th>
                <Th>Expires</Th>
                <Th>Used</Th>
                <Th>Public URL</Th>
                <Th style={{ width: 80 }}></Th>
              </tr>
            </thead>
            <tbody>
              {shares.map((s) => {
                const st = shareStatus(s);
                const canRevoke = !s.revokedAt && (!s.expiresAt || new Date(s.expiresAt) > new Date());
                return (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <Td>{fmtDate(s.createdAt)}</Td>
                    <Td>{s.recipientEmail || <span style={{ color: theme.textLight }}>—</span>}</Td>
                    <Td>
                      {s.kind === 'system' ? (
                        <span style={{ color: theme.textLight }}>System link</span>
                      ) : canRevoke ? (
                        <select
                          aria-label="What this link can do"
                          value={ACCESS.some(([k]) => k === s.permission) ? s.permission : 'view'}
                          disabled={switchingId === s.id}
                          onChange={(e) => switchAccess(s.id, e.target.value)}
                          style={{ ...input, width: 'auto', padding: '4px 8px', fontSize: 12, opacity: switchingId === s.id ? 0.5 : 1 }}
                        >
                          {ACCESS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                      ) : accessLabel(s.permission)}
                    </Td>
                    <Td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                        background: st.color + '22', color: st.color,
                      }}>{st.label}</span>
                    </Td>
                    <Td>{s.expiresAt ? fmtDate(s.expiresAt) : 'Never'}</Td>
                    <Td>
                      <div>{s.usedCount || 0}×</div>
                      {s.lastUsedAt && (
                        <div style={{ fontSize: 10, color: theme.textLight }}>last {fmtDate(s.lastUsedAt)}</div>
                      )}
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <code style={{
                          fontSize: 11, color: theme.textLight,
                          maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          display: 'inline-block',
                        }} title={s.url}>{s.url}</code>
                        <button
                          onClick={() => copyUrl(s)}
                          style={{
                            ...btnSecondary, padding: '4px 8px', fontSize: 11,
                            display: 'inline-flex', alignItems: 'center',
                          }}
                          title="Copy link"
                        >{copiedId === s.id ? <Check size={12} /> : <Copy size={12} />}</button>
                      </div>
                    </Td>
                    <Td style={{ textAlign: 'right' }}>
                      {canRevoke && (
                        <button
                          onClick={() => revoke(s.id)}
                          disabled={revokingId === s.id}
                          style={{
                            ...btnSecondary, padding: '4px 10px', fontSize: 11,
                            color: theme.danger, opacity: revokingId === s.id ? 0.5 : 1,
                          }}
                        >{revokingId === s.id ? '…' : 'Revoke'}</button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <ShareReportModal
          projectId={projectId}
          onClose={() => setShowModal(false)}
          onCreated={() => load()}
        />
      )}
    </div>
  );
}

function Th({ children, style }) {
  return (
    <th style={{
      textAlign: 'left', padding: '10px 12px',
      fontSize: 11, fontWeight: 700, color: theme.textLight,
      textTransform: 'uppercase', letterSpacing: '0.04em',
      ...style,
    }}>{children}</th>
  );
}
function Td({ children, style }) {
  return <td style={{ padding: '10px 12px', verticalAlign: 'middle', ...style }}>{children}</td>;
}
